import React, { useEffect, useState } from 'react';
import axios from 'axios';

const Locations = ({ authUser }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', is_local: false, keywords: [] });
  const [keywordInput, setKeywordInput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadLocations = async () => {
    try {
      const res = await axios.get('/api/locations');
      setLocations(res.data);
    } catch (err) {
      setError('Failed to load locations');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadLocations();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editing) {
        // Update existing location
        await axios.put(`/api/locations/${editing.id}`, formData);
        setSuccess('Location updated successfully');
      } else {
        // Create new location
        await axios.post('/api/locations', formData);
        setSuccess('Location created successfully');
      }
      
      setFormData({ name: '', is_local: false, keywords: [] });
      setKeywordInput('');
      setEditing(null);
      loadLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save location');
    }
  };

  const handleEdit = (location) => {
    setEditing(location);
    setFormData({
      name: location.name,
      is_local: location.is_local,
      keywords: Array.isArray(location.keywords) ? location.keywords : []
    });
    setKeywordInput('');
  };

  const handleCancel = () => {
    setEditing(null);
    setFormData({ name: '', is_local: false, keywords: [] });
    setKeywordInput('');
    setError('');
    setSuccess('');
  };

  const handleAddKeyword = () => {
    const value = keywordInput.trim();
    if (!value) return;
    const exists = formData.keywords.some(
      (keyword) => keyword.toLowerCase() === value.toLowerCase()
    );
    if (exists) {
      setKeywordInput('');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      keywords: [...prev.keywords, value]
    }));
    setKeywordInput('');
  };

  const handleRemoveKeyword = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleDelete = async (location) => {
    if (!window.confirm(`Are you sure you want to delete "${location.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`/api/locations/${location.id}`);
      setSuccess('Location deleted successfully');
      loadLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete location');
    }
  };

  if (loading) {
    return <div className="alert alert-info">Loading locations...</div>;
  }

  return (
    <div>
      <div className="mb-3">
        <h3 className="mb-3">Locations</h3>
        
        {/* Form */}
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">{editing ? 'Edit Location' : 'Add New Location'}</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter location name"
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="is_local"
                        checked={formData.is_local}
                        onChange={(e) => setFormData({ ...formData, is_local: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="is_local">
                        Is Local
                      </label>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="location-keyword-input">
                      Keywords
                    </label>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      <input
                        id="location-keyword-input"
                        type="text"
                        className="form-control"
                        style={{ maxWidth: '260px' }}
                        value={keywordInput}
                        placeholder="Add keyword (e.g. Little Rock)"
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddKeyword();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-success"
                        onClick={handleAddKeyword}
                      >
                        Add Keyword
                      </button>
                    </div>
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {formData.keywords.length === 0 ? (
                        <span className="text-muted">No keywords added yet.</span>
                      ) : (
                        formData.keywords.map((keyword, index) => (
                          <span className="badge bg-success d-flex align-items-center" key={`${keyword}-${index}`}>
                            <span>{keyword}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-white ms-2 p-0"
                              onClick={() => handleRemoveKeyword(index)}
                              aria-label={`Remove keyword ${keyword}`}
                            >
                              ×
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                    <small className="text-muted d-block mt-2">
                      Keywords help the system detect local coverage for this location. Matching is case-insensitive.
                    </small>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-success">
                  {editing ? 'Update' : 'Create'} Location
                </button>
                {editing && (
                  <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Messages */}
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
      </div>

      {/* Locations List */}
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">All Locations</h5>
        </div>
        <div className="card-body">
          {locations.length === 0 ? (
            <p className="text-muted">No locations found. Create your first location above.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Keywords</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.map((location) => (
                    <tr key={location.id}>
                      <td>
                        <strong>{location.name}</strong>
                      </td>
                      <td>
                        <span className={`badge ${location.is_local ? 'bg-success' : 'bg-secondary'}`}>
                          {location.is_local ? 'Local' : 'National'}
                        </span>
                      </td>
                      <td>
                        {location.keywords && location.keywords.length > 0 ? (
                          <div className="d-flex flex-wrap gap-2">
                            {location.keywords.map((keyword, index) => (
                              <span className="badge bg-light border border-success text-success" key={`${location.id}-keyword-${index}`}>
                                {keyword}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {location.created_at ? new Date(location.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleEdit(location)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(location)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Locations;
